/**
 * What an approval card says about the action it is gating.
 *
 * A port of the naming and formatting inside `bimgo-ai-web`'s
 * `HitlApprovalSection`, pulled out of the component because it decides **what a
 * user is told they are approving**, and a rule that only exists inside JSX is a
 * rule no test can hold to.
 *
 * The rule this exists to enforce: **the card never dumps the raw arguments.**
 * The first version of this panel rendered `JSON.stringify(args, null, 2)` for
 * every action, which for `send_code_to_revit` is the entire generated C# —
 * hundreds of lines of JSON-escaped source in a box above the composer, with the
 * Approve button pushed off screen. The agent already sends a `displayName` and
 * a `description` for exactly this, and the code itself has a home: the "Show
 * Source Code" control on the tool row once it has run.
 *
 * The counter-argument is real and worth stating, because it is the reason the
 * dump was there: you are approving *these* arguments, and a summary that omits
 * a field is how the wrong thing gets approved. What settles it is that the
 * omitted field here is the one nobody can audit by eye — a wall of generated
 * C# is not review, it is the appearance of review — while the fields that
 * decide *what happens* (which skill, which parameters) are all shown.
 */
import { describeAskUser } from "./askUser";
import { humanizeToolName, TOOL_NAMES } from "./toolLabel";
import type { ActionRequest, AgentStep, DecisionKind, ResumeDecision } from "./chat";

export type ApprovalKind = "generate-code" | "execute-skill" | "commit-skill" | "save-skill" | "ask-user" | "other";

export interface ApprovalCard {
  kind: ApprovalKind;
  /** i18n key for the kicker above the title, or null when there is none. */
  kickerKey: string | null;
  /**
   * The name to show. Empty when the action carried none, in which case the
   * caller renders `fallbackKey` — kept apart so this module needs no locale.
   */
  title: string;
  fallbackKey: string;
  /** The agent's own one-line explanation, when it sent one. */
  description: string | null;
  /** `name: value` pairs for a skill run, or null. Never the code. */
  params: string | null;
}

const GENERATES_CODE: readonly string[] = [
  TOOL_NAMES.SEND_CODE_TO_REVIT,
  TOOL_NAMES.SEND_CODE_TO_AUTOCAD,
  TOOL_NAMES.SEND_SKILL_CANDIDATE,
];

/**
 * The agent's own one-line note about an action, if it sent one — and not the
 * HITL middleware's boilerplate, which is the description prefix followed by
 * `Tool: …` and `Args: {…}`, the full arguments included. That text is for a
 * log, not a card: for any tool it puts the whole payload on screen.
 */
function agentNote(action: ActionRequest): string | null {
  const text = action.description?.trim() ?? "";
  if (!text) return null;
  if (/^Tool:\s/m.test(text) || /^Args:\s/m.test(text)) return null;
  return text;
}

function str(args: Record<string, unknown> | undefined, key: string): string {
  const value = args?.[key];
  return typeof value === "string" ? value.trim() : "";
}

/**
 * A skill folder name, as a title.
 *
 * `wall-schedule-1737059812` → "Wall Schedule". The trailing timestamp is how
 * the store keeps names unique on disk and means nothing to a reader; ten digits
 * or more, so a skill legitimately ending in a small number keeps it.
 */
export function skillTitleFromPath(skillPath: string): string {
  return skillPath
    .replace(/-\d{10,}$/, "")
    .split("-")
    .filter(Boolean)
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(" ");
}

/**
 * A skill in the new store, `Skills/<Vendor>/[<folder>/]<skill>-<hash>/<year>` (agent
 * `skill-v2/run-skill.ts`): titled from its folder without the hash, with the software and
 * year it runs on. Null for a Revit year-store path.
 */
export function vendorSkillOf(skillPath: string): { title: string; where: string } | null {
  const segments = skillPath.replace(/\\/g, "/").replace(/\/SKILL\.md$/i, "").split("/").filter(Boolean);
  if (segments[0] !== "Skills" || segments.length < 4) return null;
  const vendor = segments[1]!;
  const year = /^\d{4}$/.test(segments.at(-1)!) ? segments.pop()! : null;
  const folder = segments.at(-1)!.replace(/-[0-9a-f]{8}$/, "");
  return { title: skillTitleFromPath(folder), where: year ? `${vendor} ${year}` : vendor };
}

/**
 * Skill parameters, flattened to one line.
 *
 * Shown because they are what the skill will actually do — a schedule of the
 * wrong level is a different action, not a different rendering of the same one.
 * The agent sends them as an object or as a JSON string depending on the tool,
 * and a string that will not parse is dropped rather than shown raw: this line
 * is read as "these are the parameters", so showing an unparsed blob there
 * misrepresents it.
 */
export function formatSkillParams(raw: unknown): string | null {
  let params: Record<string, unknown> | null = null;

  if (typeof raw === "string") {
    try {
      const parsed: unknown = JSON.parse(raw);
      if (parsed && typeof parsed === "object" && !Array.isArray(parsed)) {
        params = parsed as Record<string, unknown>;
      }
    } catch {
      return null;
    }
  } else if (raw && typeof raw === "object" && !Array.isArray(raw)) {
    params = raw as Record<string, unknown>;
  }

  if (!params) return null;
  const entries = Object.entries(params);
  if (entries.length === 0) return null;
  return entries.map(([key, value]) => `${key}: ${String(value)}`).join(", ");
}

/** What the card should say about one pending action. */
/**
 * The old web's four decided cards, from what was sent back: a plan-mode
 * "request changes" is revised, a plan-mode stop is rejected, an answer to
 * `ask_user` is answered, and otherwise approved or rejected as the user said.
 */
export function decisionKind(decision: ResumeDecision): DecisionKind {
  if (decision.intent === "revise") return "revised";
  if (decision.intent === "reject") return "rejected";
  if (typeof decision.answer === "string") return "answered";
  return decision.approved ? "approved" : "rejected";
}

/** The row that stays in the flow where an approval was decided. */
export function decisionStep(action: ActionRequest, decision: ResumeDecision): AgentStep {
  const card = describeApproval(action);
  const kind = decisionKind(decision);
  return {
    tool: action.name,
    status: "completed",
    decision: {
      kind, title: card.title, fallbackKey: card.fallbackKey,
      ...(kind === "answered" ? { answer: decision.answer } : {}),
    },
  };
}

export function describeApproval(action: ActionRequest): ApprovalCard {
  const args = action.args;

  if (GENERATES_CODE.includes(action.name)) {
    return {
      kind: "generate-code",
      kickerKey: "hitl.generateCode",
      // `displayName` is what the agent names the change for a human; `name` is
      // the slug it will save under. Either beats the tool's own name, which
      // says how it happens rather than what happens.
      title: str(args, "displayName") || str(args, "name"),
      fallbackKey: "message.toolGenerateCode",
      description: str(args, "description") || null,
      params: null,
    };
  }

  if (action.name === TOOL_NAMES.EXECUTE_CLOUD_SKILL) {
    return {
      kind: "execute-skill",
      kickerKey: "hitl.executeSkill",
      // A cloud skill is addressed by UUID, so its friendly name has to come
      // from `displayName` — there is nothing readable in the id.
      title: str(args, "displayName"),
      fallbackKey: "message.toolUseTool",
      description: str(args, "description") || null,
      params: formatSkillParams(args?.["skillParameters"]),
    };
  }

  if (action.name === TOOL_NAMES.EXECUTE_SKILL_FROM_PATH) {
    const path = str(args, "skillPath");
    const vendor = vendorSkillOf(path);
    return {
      kind: "execute-skill",
      kickerKey: "hitl.executeSkill",
      title: str(args, "displayName") || (vendor ? vendor.title : path ? skillTitleFromPath(path) : ""),
      fallbackKey: "message.toolUseTool",
      description: str(args, "description") || vendor?.where || null,
      params: formatSkillParams(args?.["skillParameters"]),
    };
  }

  if (action.name === TOOL_NAMES.COMMIT_SKILL_VERSION) {
    // The save gate of the Edit-with-AI flow: the kicker says what kind of
    // thing is about to happen, the title is the agent's own one-line summary
    // of the change, and the footnote lists which metadata fields move with it.
    // The code itself is deliberately absent — the rule at the top of this file.
    const artifact = action.args?.["artifact"];
    const changed =
      artifact && typeof artifact === "object" && !Array.isArray(artifact)
        ? Object.keys(artifact as Record<string, unknown>).filter(
            (key) => (artifact as Record<string, unknown>)[key] !== undefined,
          )
        : [];
    return {
      kind: "commit-skill",
      kickerKey: "skillEdit.titleCommit",
      title: str(args, "summary"),
      fallbackKey: "skillEdit.titleCommit",
      description: null,
      params: changed.length > 0 ? changed.join(", ") : null,
    };
  }

  if (action.name === TOOL_NAMES.SAVE_SKILL) {
    // The chat's "save this as a skill" gate (item BU): the skill's name and
    // its description, as the old plugin's generate-code card showed them —
    // never the artifact. The tool's arguments *are* the artifact (the C#, the
    // parameter schema, the transaction mode), and the card was showing all of
    // it through the middleware's own description text.
    return {
      kind: "save-skill",
      kickerKey: "hitl.saveSkill",
      title: str(args, "displayName") || str(args, "name"),
      fallbackKey: "hitl.saveSkill",
      description: str(args, "description") || null,
      params: null,
    };
  }

  if (action.name === TOOL_NAMES.ASK_USER) {
    // Plan mode's clarifying question (#99). The question is the title — it is what the
    // decided row names once the user has answered, as the web's "Answered" card does —
    // and nothing else: the middleware's description is the serialized arguments, and the
    // options belong to the live card, not to the row that records the answer.
    return {
      kind: "ask-user",
      kickerKey: null,
      title: describeAskUser(action).question,
      fallbackKey: "hitl.askUserTitle",
      description: null,
      params: null,
    };
  }

  // Anything else: the tool's own name, humanised. No kicker — there is nothing
  // to categorise it as that the name does not already say.
  return {
    kind: "other",
    kickerKey: null,
    title: humanizeToolName(action.name),
    fallbackKey: "message.toolUseTool",
    description: agentNote(action) || str(args, "description") || null,
    params: null,
  };
}
